import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Filter, PawPrint, Heart } from 'lucide-react';
import KittenCard from '@/components/KittenCard';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AvailableKittens() {
  const [kittens, setKittens] = useState([]);
  const [filteredKittens, setFilteredKittens] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [breedFilter, setBreedFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');

  const fetchKittens = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kittens`);
      setKittens(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch kittens:', error);
      }
    }
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...kittens];

    if (statusFilter !== 'all') {
      filtered = filtered.filter((k) => k.status === statusFilter);
    }

    if (breedFilter !== 'all') {
      filtered = filtered.filter((k) => k.breed === breedFilter);
    }

    if (genderFilter !== 'all') {
      filtered = filtered.filter((k) => k.gender === genderFilter);
    }

    const statusOrder = { 'Available': 0, 'Reserved': 1, 'Sold': 2 };
    filtered.sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3));

    setFilteredKittens(filtered);
  }, [kittens, statusFilter, breedFilter, genderFilter]);

  useEffect(() => {
    fetchKittens();
  }, [fetchKittens]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const resetFilters = () => {
    setStatusFilter('all');
    setBreedFilter('all');
    setGenderFilter('all');
  };

  return (
    <main className="py-16 lg:py-24 min-h-screen bg-[#FAF8F5]">
      <SEO 
        title="Available Kittens | KittenState"
        description="Browse our Scottish Fold and Scottish Straight kittens currently available for adoption at KittenState. View photos, learn about each kitten's personality, and reserve your new companion."
        keywords="Scottish Fold kittens for sale, Scottish Straight kittens for adoption, available kittens Pennsylvania, KittenState kittens"
        url="https://kittenstate.com/kittens"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
            Find Your Perfect Companion
          </p>
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-stone-900">
              Available Kittens
            </h1>
            <Heart className="w-8 h-8 text-[#E85D75]" />
          </div>
          <p className="text-base lg:text-lg text-stone-600 max-w-2xl mx-auto">
            Browse our beautiful Scottish Fold and Scottish Straight kittens looking for their
            forever homes.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 border border-rose-100/50 shadow-sm mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#E85D75]" />
            <h3 className="text-lg font-semibold text-stone-800">Filter Kittens</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="rounded-xl border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Reserved">Reserved</SelectItem>
                  <SelectItem value="Sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">Breed</label>
              <Select value={breedFilter} onValueChange={setBreedFilter}>
                <SelectTrigger data-testid="breed-filter" className="rounded-xl border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20">
                  <SelectValue placeholder="All Breeds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Breeds</SelectItem>
                  <SelectItem value="Scottish Fold">Scottish Fold</SelectItem>
                  <SelectItem value="Scottish Straight">Scottish Straight</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2 block">Gender</label>
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger data-testid="gender-filter" className="rounded-xl border-rose-100 focus:border-[#E85D75] focus:ring-[#E85D75]/20">
                  <SelectValue placeholder="All Genders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                data-testid="reset-filters-button"
                onClick={resetFilters}
                variant="outline"
                className="w-full border-rose-200 text-stone-600 hover:bg-rose-50 hover:text-[#E85D75] rounded-xl"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Kittens Grid */}
        {filteredKittens.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
            {filteredKittens.map((kitten) => (
              <KittenCard key={kitten.id} kitten={kitten} />
            ))}
          </div>
        ) : (
          <div
            data-testid="no-kittens-message"
            className="text-center py-20 bg-white rounded-3xl border border-rose-100"
          >
            <PawPrint className="w-16 h-16 text-rose-200 mx-auto mb-4" />
            <h3 className="text-xl font-serif font-semibold text-stone-800 mb-2">No Kittens Available</h3>
            <p className="text-stone-600 mb-6">Check back soon or contact us for updates on upcoming litters!</p>
            <Button
              onClick={resetFilters}
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full px-8 py-5 shadow-lg shadow-[#E85D75]/25"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
